import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/request';
import type {
  WorkflowRule,
  WorkflowRuleListResponse,
} from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/api/workflowRule.types';
import { MoveWithAutomationModal } from './MoveWithAutomationModal';
import {
  deriveCurrentStageStopping,
  ruleActionsToStoppingActions,
  type StoppingAction,
} from './deriveCurrentStageStopping';
import { isImmediateFirstStep } from './firstStepTiming';
import {
  useEnqueueAutomationMove,
  type EnqueueAutomationMoveInput,
} from '@/components/automation/UnderwayPopup';

// What `performMove` resolves to. The move endpoint returns the
// pending stage-automation execution IDs it created synchronously, so
// the popup can run the undo window without racing Sidekiq. Callers
// that don't surface IDs (or "Move without automation") return void.
//
// `firstStepAlreadyRan` — set from the move response's
// `next_action` (see `firstStepAlreadyRan`): true on stage RE-ENTRY where the
// immediate first activity already ran, so the popup must not re-enqueue it (the
// BE would `already_created`-skip it anyway).
export type MoveResult = {
  pendingExecutionIds?: number[];
  firstStepAlreadyRan?: boolean;
};

export type PerformMove = (
  skipAutomation: boolean
) => Promise<MoveResult | void> | MoveResult | void;

// Metadata the confirm hook needs to enqueue the post-move trigger
// window into the popup. Only consulted on the "Move" (with
// automation) path — "Move without automation" doesn't create
// executions on BE, so the popup is irrelevant.
//
// `getEnqueueInputBase(rule)` returns everything except executionIds
// — those come back from `performMove`'s MoveResult.
export type ConfirmAndMoveUnderway = {
  getEnqueueInputBase: (
    rule: WorkflowRule
  ) => Omit<EnqueueAutomationMoveInput, 'executionIds'> | null;
};

export type ConfirmAndMoveArgs = {
  targetStageId: number | string;
  targetStageName: string;
  currentStageName?: string;
  currentStageStoppingActions?: StoppingAction[];
  applicationId?: number | string;
  performMove: PerformMove;
  // Optional — if provided, enables the popup-delayed move path.
  underway?: ConfirmAndMoveUnderway;
  // Skip the confirm modal and proceed as if the user clicked "Move"
  // (with automation) — used by rapid flows like Quick App Review.
  autoConfirm?: boolean;
};

type ModalState = {
  isOpen: boolean;
  targetStageName: string;
  targetRule: WorkflowRule | null;
  currentStageName?: string;
  currentStageStoppingActions: StoppingAction[];
  performMove: PerformMove | null;
  underway: ConfirmAndMoveUnderway | null;
};

const stageRuleKey = (stageId: string) =>
  ['workflowRule', 'stage', stageId] as const;

const fetchActiveStageRule = async (
  stageId: string
): Promise<WorkflowRule | null> => {
  const res = await api.get<WorkflowRuleListResponse>('workflow_rules', {
    params: {
      event_name: 'application.stage_changed',
      ats_job_stage_id: Number(stageId),
      status: 'active',
    },
  });
  return res.workflow_rules[0] ?? null;
};

type ApplicationShowResponse = {
  job_stage_id?: number | string;
  ats_job_stages?: unknown[];
};

const fetchApplicationForMove = async (
  applicationId: number | string
): Promise<ApplicationShowResponse | null> => {
  try {
    return await api.get<ApplicationShowResponse>(
      `ats_applications/${applicationId}`,
      { params: { include: 'stage_actions' } }
    );
  } catch {
    return null;
  }
};

export const useMoveStageWithAutomationConfirm = () => {
  const queryClient = useQueryClient();
  const enqueueAutomationMove = useEnqueueAutomationMove();
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    targetStageName: '',
    targetRule: null,
    currentStageStoppingActions: [],
    performMove: null,
    underway: null,
  });
  const [isMoving, setIsMoving] = useState(false);

  const closeModal = useCallback(() => {
    setModal((s) => ({
      ...s,
      isOpen: false,
      performMove: null,
      underway: null,
    }));
  }, []);

  const confirmAndMove = useCallback(
    async ({
      targetStageId,
      targetStageName,
      currentStageName,
      currentStageStoppingActions = [],
      applicationId,
      performMove,
      underway,
      autoConfirm = false,
    }: ConfirmAndMoveArgs) => {
      const stageIdString = String(targetStageId);
      const rule = await queryClient.fetchQuery({
        queryKey: stageRuleKey(stageIdString),
        queryFn: () => fetchActiveStageRule(stageIdString),
      });

      if (!rule) {
        await performMove(false);
        return;
      }

      // Auto-confirm: move with automation + enqueue the undo popup,
      // never opening the modal.
      if (autoConfirm) {
        const result = await performMove(false);
        // Only the immediate-first-step case belongs in the undo popup; a
        // delayed first step is scheduled and fired by the BE itself. On stage
        // re-entry where that first step already ran, skip it (BE would
        // `already_created`-skip it and it must not re-show).
        if (
          underway &&
          isImmediateFirstStep(rule) &&
          !result?.firstStepAlreadyRan
        ) {
          const base = underway.getEnqueueInputBase(rule);
          const executionIds = result?.pendingExecutionIds ?? [];
          if (base && executionIds.length > 0) {
            enqueueAutomationMove({ ...base, executionIds });
          }
        }
        return;
      }

      let derivedCurrentStageName = currentStageName;
      let derivedStoppingActions = currentStageStoppingActions;
      if (applicationId !== undefined && applicationId !== null) {
        const application = await fetchApplicationForMove(applicationId);
        if (application) {
          const derived = deriveCurrentStageStopping(application);
          derivedCurrentStageName = derived.currentStageName;
          derivedStoppingActions = derived.currentStageStoppingActions;

          // Nothing in flight — fall back to the current stage's
          // active rule so its automation still shows in the modal.
          const currentStageId = application.job_stage_id;
          if (
            derivedStoppingActions.length === 0 &&
            currentStageId !== undefined &&
            currentStageId !== null
          ) {
            const currentRule = await queryClient.fetchQuery({
              queryKey: stageRuleKey(String(currentStageId)),
              queryFn: () => fetchActiveStageRule(String(currentStageId)),
            });
            if (currentRule) {
              derivedStoppingActions =
                ruleActionsToStoppingActions(currentRule);
            }
          }
        }
      }

      setModal({
        isOpen: true,
        targetStageName,
        targetRule: rule,
        currentStageName: derivedCurrentStageName,
        currentStageStoppingActions: derivedStoppingActions,
        performMove,
        underway: underway ?? null,
      });
    },
    [queryClient, enqueueAutomationMove]
  );

  const runMove = useCallback(
    async (skipAutomation: boolean) => {
      if (!modal.performMove) return;
      setIsMoving(true);
      try {
        // Always fire the move first. BE moves the candidate(s)
        // immediately; for stage rules BE also creates the pending
        // workflow_executions synchronously and returns their IDs.
        const result = await modal.performMove(skipAutomation);

        // Move WITH automation + underway metadata + active rule
        // → enqueue the IDs BE returned so the popup can run the 15s
        // trigger/cancel window. Skip on re-entry where the immediate first
        // step already ran (BE would `already_created`-skip it).
        if (
          !skipAutomation &&
          modal.underway &&
          modal.targetRule &&
          isImmediateFirstStep(modal.targetRule) &&
          !result?.firstStepAlreadyRan
        ) {
          const base = modal.underway.getEnqueueInputBase(modal.targetRule);
          const executionIds = result?.pendingExecutionIds ?? [];
          if (base && executionIds.length > 0) {
            enqueueAutomationMove({ ...base, executionIds });
          }
        }
        closeModal();
      } finally {
        setIsMoving(false);
      }
    },
    [modal, closeModal, enqueueAutomationMove]
  );

  const modalElement = (
    <MoveWithAutomationModal
      isOpen={modal.isOpen}
      onClose={closeModal}
      targetStageName={modal.targetStageName}
      targetRule={modal.targetRule}
      currentStageName={modal.currentStageName}
      currentStageStoppingActions={modal.currentStageStoppingActions}
      isMoving={isMoving}
      onCancel={closeModal}
      onMoveWithoutAutomation={() => void runMove(true)}
      onMove={() => void runMove(false)}
    />
  );

  return { confirmAndMove, modalElement };
};
